/**
 * Voice Recording Panel Component
 * ================================
 * UI for recording voice notes with controls and waveform animation
 */

import { useEffect, useState } from 'react';
import {
  Mic,
  Square,
  Pause,
  Play,
  RotateCcw,
  Send,
  AlertCircle,
  Volume2,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useVoiceRecording } from '@/hooks/useVoiceRecording';

interface VoiceRecordingPanelProps {
  onSubmit: (audioBlob: Blob, duration: number) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function VoiceRecordingPanel({ onSubmit, onCancel, isSubmitting }: VoiceRecordingPanelProps) {
  const {
    recordingState,
    audioURL,
    audioBlob,
    duration,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetRecording,
    error,
  } = useVoiceRecording();

  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle audio playback
  useEffect(() => {
    if (audioURL && !audioElement) {
      const audio = new Audio(audioURL);
      audio.onended = () => setIsPlaying(false);
      setAudioElement(audio);
    }

    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.onended = null;
      }
    };
  }, [audioURL]);

  const togglePlayback = () => {
    if (!audioElement) return;

    if (isPlaying) {
      audioElement.pause();
      setIsPlaying(false);
    } else {
      audioElement.play();
      setIsPlaying(true);
    }
  };

  const handleSubmit = async () => {
    if (audioBlob) {
      await onSubmit(audioBlob, duration);
    }
  };

  const handleReset = () => {
    if (audioElement) {
      audioElement.pause();
      setIsPlaying(false);
      setAudioElement(null);
    }
    resetRecording();
  };

  return (
    <Card className="p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Voice Recording</h3>
          {recordingState === 'idle' && !audioBlob && (
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Recording Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Recording Status */}
        <div className="text-center">
          {recordingState !== 'idle' && (
            <div className="mb-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-full">
                <div className={`w-2 h-2 bg-red-500 rounded-full ${recordingState === 'recording' ? 'animate-pulse' : ''}`} />
                <span className="font-medium text-sm">
                  {recordingState === 'recording' ? 'Recording...' : 'Paused'}
                </span>
              </div>
            </div>
          )}

          {/* Duration Display */}
          <div className="text-4xl font-mono font-bold text-gray-900 mb-6">
            {formatDuration(duration)}
          </div>

          {/* Waveform Animation */}
          {recordingState === 'recording' && (
            <div className="flex items-center justify-center gap-1 h-16 mb-6">
              {Array.from({ length: 20 }).map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-blue-500 rounded-full animate-wave"
                  style={{
                    animationDelay: `${i * 0.05}s`,
                    height: `${20 + Math.random() * 40}%`,
                  }}
                />
              ))}
            </div>
          )}

          {/* Recorded Audio Playback */}
          {audioBlob && recordingState === 'idle' && (
            <div className="mb-6">
              <Button
                variant="outline"
                size="lg"
                onClick={togglePlayback}
                className="w-32"
              >
                {isPlaying ? (
                  <>
                    <Pause className="w-5 h-5 mr-2" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-2" />
                    Play
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-3">
          {recordingState === 'idle' && !audioBlob && (
            <Button
              size="lg"
              onClick={startRecording}
              disabled={!!error}
              className="px-8"
            >
              <Mic className="w-5 h-5 mr-2" />
              Start Recording
            </Button>
          )}

          {recordingState === 'recording' && (
            <>
              <Button
                variant="outline"
                size="lg"
                onClick={pauseRecording}
              >
                <Pause className="w-5 h-5 mr-2" />
                Pause
              </Button>
              <Button
                variant="danger"
                size="lg"
                onClick={stopRecording}
              >
                <Square className="w-5 h-5 mr-2" />
                Stop
              </Button>
            </>
          )}

          {recordingState === 'paused' && (
            <>
              <Button
                size="lg"
                onClick={resumeRecording}
              >
                <Play className="w-5 h-5 mr-2" />
                Resume
              </Button>
              <Button
                variant="danger"
                size="lg"
                onClick={stopRecording}
              >
                <Square className="w-5 h-5 mr-2" />
                Stop
              </Button>
            </>
          )}

          {audioBlob && recordingState === 'idle' && (
            <>
              <Button
                variant="outline"
                size="lg"
                onClick={handleReset}
                disabled={isSubmitting}
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Re-record
              </Button>
              <Button
                size="lg"
                onClick={handleSubmit}
                disabled={isSubmitting}
                isLoading={isSubmitting}
              >
                <Send className="w-5 h-5 mr-2" />
                {isSubmitting ? 'Submitting...' : 'Submit Recording'}
              </Button>
            </>
          )}
        </div>

        {/* Recording Tips */}
        {recordingState === 'idle' && !audioBlob && !error && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-start gap-2">
              <Volume2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 mb-1">Recording Tips</p>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Speak clearly and at a normal pace</li>
                  <li>• Mention tooth numbers and measurements</li>
                  <li>• Example: "Tooth 14, MB 3, B 4, DB 3..."</li>
                  <li>• You can pause and resume anytime</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes wave {
          0%, 100% {
            transform: scaleY(0.5);
          }
          50% {
            transform: scaleY(1);
          }
        }
        .animate-wave {
          animation: wave 1s ease-in-out infinite;
        }
      `}</style>
    </Card>
  );
}
